/* Stacks — commerce behaviour
   cart drawer (Section Rendering API) · product page (variants, media, zoom, sticky bar, share)
   predictive search · collection filters · recommendations · drawer focus trap */
(() => {
  const $ = (s, r = document) => r.querySelector(s);
  const $$ = (s, r = document) => [...r.querySelectorAll(s)];
  const S = window.Stacks || {};
  const money = (c) => (window.formatMoney ? formatMoney(c) : (c / 100).toFixed(2));
  const parse = (html) => new DOMParser().parseFromString(html, 'text/html');

  /* ================================================================ drawers: open helper + focus trap */
  const openDrawer = (id) => {
    const d = document.getElementById(id);
    if (!d) return;
    // the cart's lines settle in on open only — not again on every quantity change
    const cd = $('.cd', d);
    if (cd && !d.classList.contains('is-open')) {
      cd.classList.add('is-entering');
      clearTimeout(cd._enter); cd._enter = setTimeout(() => cd.classList.remove('is-entering'), 900);
    }
    d.classList.add('is-open'); d.setAttribute('aria-hidden', 'false');
    document.body.style.overflow = 'hidden';
    d._returnFocus = document.activeElement;
    setTimeout(() => ($('[data-autofocus]', d) || $('button, a[href], input', $('.drawer__panel', d)))?.focus(), 60);
  };
  window.openDrawer = openDrawer;

  document.addEventListener('keydown', (e) => {
    if (e.key !== 'Tab') return;
    const d = $('.drawer.is-open');
    if (!d) return;
    const f = $$('a[href], button:not([disabled]), input:not([type=hidden]), select, textarea, [tabindex]:not([tabindex="-1"])', $('.drawer__panel', d)).filter(el => el.offsetParent !== null);
    if (!f.length) return;
    const first = f[0], last = f[f.length - 1];
    if (e.shiftKey && document.activeElement === first) { e.preventDefault(); last.focus(); }
    else if (!e.shiftKey && document.activeElement === last) { e.preventDefault(); first.focus(); }
  });
  // return focus when any drawer closes
  new MutationObserver((muts) => muts.forEach(m => {
    const d = m.target;
    if (m.attributeName === 'class' && d.classList.contains('drawer') && !d.classList.contains('is-open') && d._returnFocus) {
      d._returnFocus.focus?.(); d._returnFocus = null;
    }
  })).observe(document.body, { attributes: true, subtree: true, attributeFilter: ['class'] });

  /* ================================================================ cart */
  const setCount = (n) => $$('[data-cart-count]').forEach(el => { el.textContent = n; el.hidden = n === 0; });
  // a print's quantity is its price — count each print line as one print
  const lineCount = (cart) => (cart.items || []).reduce((n, i) => n + (i.handle === S.printHandle ? 1 : i.quantity), 0);
  window.cartLineCount = lineCount;

  const renderDrawer = (html) => {
    if (!html) return;
    const fresh = $('.cd', parse(html));
    const panel = $('#cart-drawer .cd');
    if (fresh && panel) panel.innerHTML = fresh.innerHTML;
  };

  const sectionsToRender = () => {
    const ids = ['cart-drawer'];
    const main = $('[data-cart-section]');
    if (main) ids.push(main.dataset.cartSection);
    return ids;
  };

  const applySections = (sections = {}) => {
    renderDrawer(sections['cart-drawer']);
    const main = $('[data-cart-section]');
    if (main && sections[main.dataset.cartSection]) {
      const fresh = $('[data-cart-section]', parse(sections[main.dataset.cartSection]));
      if (fresh) main.replaceWith(fresh);
    }
  };

  window.addToCart = async (body, { button } = {}) => {
    const label = button?.innerHTML;
    if (button) { button.disabled = true; button.innerHTML = '<span class="spin"></span>'; }
    try {
      if (body instanceof FormData) { body.append('sections', sectionsToRender().join(',')); body.append('sections_url', location.pathname); }
      const res = await fetch(S.routes.cartAdd + '.js', { method: 'POST', body, headers: { Accept: 'application/json', 'X-Requested-With': 'XMLHttpRequest' } });
      const data = await res.json();
      if (!res.ok || data.status) throw new Error(data.description || data.message || S.t.error);
      applySections(data.sections);
      const cart = await fetch((S.routes.cart || '/cart') + '.js').then(r => r.json());
      setCount(lineCount(cart));
      if (S.cartType === 'page') location.href = S.routes.cart;
      else if (S.cartType === 'notice' || !$('#cart-drawer')) window.toast?.(`${S.t.added} <a href="${S.routes.cart}">→</a>`);
      else openDrawer('cart-drawer');
      return data;
    } catch (err) {
      window.toast?.(err.message || S.t.error);
      throw err;
    } finally {
      if (button) { button.disabled = false; button.innerHTML = label; }
    }
  };

  // used by the quote tool after its own (progress-tracked) upload
  window.refreshCartUI = async ({ open = true } = {}) => {
    const [sections, cart] = await Promise.all([
      fetch(`${location.pathname}?sections=${sectionsToRender().join(',')}`).then(r => r.json()).catch(() => ({})),
      fetch((S.routes.cart || '/cart') + '.js').then(r => r.json())
    ]);
    applySections(sections);
    setCount(lineCount(cart));
    if (open && S.cartType !== 'page' && $('#cart-drawer')) openDrawer('cart-drawer');
    else if (open) location.href = S.routes.cart;
  };

  let cartChanges = Promise.resolve();
  const changeLine = (id,quantity,busyEl) => {
    cartChanges = cartChanges.catch(()=>{}).then(()=>changeLineNow(id,quantity,busyEl));
    return cartChanges;
  };
  const changeLineNow = async (id, quantity, busyEl) => {
    busyEl?.classList.add(quantity === 0 && busyEl.matches('.cd__line') ? 'is-removing' : 'is-busy');
    try {
      const res = await fetch(S.routes.cartChange + '.js', {
        method: 'POST', headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
        body: JSON.stringify({ id, quantity, sections: sectionsToRender(), sections_url: location.pathname })
      });
      const data = await res.json();
      if (!res.ok) throw new Error(data.description || S.t.error);
      applySections(data.sections);
      setCount(lineCount(data));
    } catch (err) { window.toast?.(err.message); }
    finally { busyEl?.classList.remove('is-busy', 'is-removing'); }
  };

  /* ================================================================ coupon codes
     Codes live on the cart (/cart/update.js { discount }), so checkout picks
     them up on its own. Shopify accepts any string there and only marks it
     not applicable, so we read the verdict back and say so. */
  const setCodes = async (codes, form) => {
    const btn = form && $('.cpn__go', form);
    if (btn) btn.disabled = true;
    try {
      const res = await fetch(S.routes.cartUpdate + '.js', {
        method: 'POST', headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
        body: JSON.stringify({ discount: codes.join(','), sections: sectionsToRender(), sections_url: location.pathname })
      });
      const data = await res.json();
      if (!res.ok) throw new Error(data.description || S.t.error);
      applySections(data.sections);
      return data;
    } finally { if (btn) btn.disabled = false; }
  };
  const cpnMsg = (text, kind, keep) => {
    $$('[data-cpn]').forEach((box) => {
      const m = $('[data-cpn-msg]', box);
      m.textContent = text; m.className = 'cpn__msg' + (kind ? ' is-' + kind : '');
      $('details', box)?.setAttribute('open', '');
      if (keep) $('[name=code]', box).value = keep;
    });
  };
  document.addEventListener('submit', async (e) => {
    const form = e.target.closest('[data-cpn-form]');
    if (!form) return;
    e.preventDefault();
    const code = form.code.value.trim().toUpperCase();
    if (!code) { cpnMsg(S.t.couponEmpty, 'err'); form.code.focus(); return; }
    const have = (form.closest('[data-cpn]').dataset.codes || '').split(',').filter(Boolean);
    try {
      const data = await setCodes([...new Set([...have, code])], form);
      const hit = (data.discount_codes || []).find((d) => d.code.toUpperCase() === code);
      if (hit && hit.applicable) cpnMsg(S.t.couponOk, 'ok');
      else {
        await setCodes(have);   // don't leave a dead code sitting on the cart
        cpnMsg(S.t.couponBad, 'err', code);
      }
    } catch (err) { cpnMsg(err.message || S.t.error, 'err', code); }
  });
  document.addEventListener('click', async (e) => {
    const b = e.target.closest('[data-cpn-rm]');
    if (!b) return;
    const have = (b.closest('[data-cpn]').dataset.codes || '').split(',').filter(Boolean);
    b.disabled = true;
    try { await setCodes(have.filter((c) => c !== b.dataset.cpnRm)); } catch (err) { window.toast?.(err.message); }
  });

  // AJAX product forms (product page, quick add)
  document.addEventListener('submit', (e) => {
    const form = e.target.closest('form[data-ajax-cart]');
    if (!form) return;
    e.preventDefault();
    const button = e.submitter || $(`[type=submit][form="${form.id}"]`) || $('[type=submit]', form);
    addToCart(new FormData(form), { button }).catch(() => {});
  });

  // drawer + cart page quantity controls
  document.addEventListener('click', (e) => {
    const b = e.target.closest('[data-cart-qty]');
    if (!b) return;
    changeLine(b.dataset.lineKey, Math.max(0, +b.dataset.to), b.closest('.cd__line, .line'));
  });
  const qtyTimers = new Map();
  document.addEventListener('change', (e) => {
    const inp = e.target.closest('[data-line-qty]');
    if (!inp) return;
    const key=inp.dataset.lineKey, quantity=Math.max(0,+inp.value), row=inp.closest('.line');
    clearTimeout(qtyTimers.get(key));
    qtyTimers.set(key,setTimeout(()=>{qtyTimers.delete(key);changeLine(key,quantity,row);},350));
  });
  // cart icon opens drawer instead of navigating
  document.addEventListener('click', (e) => {
    const a = e.target.closest(`a[href="${S.routes?.cart}"]`);
    if (!a || !$('#cart-drawer') || S.cartType === 'page' || a.closest('.cd') || location.pathname === S.routes.cart) return;
    if (e.metaKey || e.ctrlKey) return;
    e.preventDefault();
    openDrawer('cart-drawer');
  });

  /* ================================================================ product page */
  const initProduct = (root) => {
    if (root.dataset.productReady) return;
    root.dataset.productReady = '1';
    const json = $('[data-variants]', root);
    if (!json) return;
    const variants = JSON.parse(json.textContent);
    const sticky = $('[data-sticky-atc]', root);

    const selectMedia = (id) => {
      const target = $(`.gallery__main[data-media-id="${id}"]`, root);
      if (!target) return;
      $$('.gallery__main[data-media-id]', root).forEach(m => {
        const on = m === target; m.hidden = !on;
        if (!on) { $('video', m)?.pause(); $('iframe', m)?.contentWindow?.postMessage('{"event":"command","func":"pauseVideo","args":""}', '*'); }
      });
      $$('[data-thumb]', root).forEach(t => t.setAttribute('aria-current', t.dataset.thumb == id));
      $(`[data-thumb="${id}"]`, root)?.scrollIntoView({ block: 'nearest', inline: 'nearest', behavior: 'smooth' });
    };

    root.addEventListener('click', (e) => {
      const t = e.target.closest('[data-thumb]');
      if (t) selectMedia(t.dataset.thumb);
    });

    const update = () => {
      const fieldsets = $$('fieldset[data-option]', root);
      const chosen = fieldsets.map(fs => $('input:checked', fs)?.value);
      fieldsets.forEach((fs, i) => { const l = $('[data-opt-label]', fs); if (l) l.textContent = chosen[i]; });

      // mark values that don't exist / are sold out in combination with the other picks
      fieldsets.forEach((fs, i) => $$('input', fs).forEach(inp => {
        const test = chosen.slice(); test[i] = inp.value;
        const match = variants.find(v => v.options.every((o, k) => o === test[k]));
        inp.closest('.pill').classList.toggle('is-unavailable', !match || !match.available);
      }));

      const v = variants.find(v => v.options.every((o, i) => o === chosen[i]));
      const addBtns = $$('[data-add]', root);
      if (!v) { addBtns.forEach(b => b.disabled = true); return; }

      $$('input[name="id"]', root).forEach(i => { i.value = v.id; });
      const onSale = v.compare_at_price > v.price;
      const priceHTML = money(v.price) + (onSale ? ` <s>${money(v.compare_at_price)}</s>` : '');
      $$('[data-price]', root).forEach(p => { p.innerHTML = priceHTML; });
      /* the sale line follows the variant: some sizes may be on sale and others not */
      const row = $('[data-sale-row]', root), save = $('[data-save]', root), saving = $('[data-saving]', root);
      if (row) row.classList.toggle('is-sale', onSale);
      if (save) { save.hidden = !onSale; if (onSale) save.textContent = row.dataset.tPct.replace('[pct]', Math.floor((v.compare_at_price - v.price) * 100 / v.compare_at_price)); }
      if (saving) { saving.hidden = !onSale; if (onSale) saving.textContent = row.dataset.tAmt.replace('[amt]', money(v.compare_at_price - v.price)); }
      addBtns.forEach(b => {
        b.disabled = !v.available;
        const lbl = $('[data-add-label]', b);
        if (lbl) lbl.textContent = v.available ? b.dataset.labelAdd : b.dataset.labelSold;
      });
      $$('.pdp__dynamic', root).forEach(f => f.classList.toggle('is-disabled', !v.available));
      if (v.featured_media) selectMedia(v.featured_media.id);
      const url = new URL(location.href); url.searchParams.set('variant', v.id); history.replaceState({}, '', url);
    };
    root.addEventListener('change', (e) => { if (e.target.closest('fieldset[data-option]')) update(); });
    if ($('fieldset[data-option]', root)) update();

    // zoom
    const dlg = $('[data-zoom-dialog]', root);
    root.addEventListener('click', (e) => {
      const z = e.target.closest('[data-zoom]');
      if (!z || !dlg?.showModal) return;
      const img = $('[data-zoom-img]', dlg);
      img.src = z.dataset.zoom;
      dlg.classList.remove('is-zoomed');
      dlg.showModal();
    });
    dlg?.addEventListener('click', (e) => {
      if (e.target.closest('[data-zoom-close]') || e.target === dlg) { dlg.close(); return; }
      if (e.target.matches('[data-zoom-img]')) {
        const r = e.target.getBoundingClientRect();
        e.target.style.transformOrigin = `${((e.clientX - r.left) / r.width) * 100}% ${((e.clientY - r.top) / r.height) * 100}%`;
        dlg.classList.toggle('is-zoomed');
      }
    });

    // swipe between images on touch
    const stage = $('.gallery__stage', root);
    let x0 = null;
    stage?.addEventListener('touchstart', (e) => { x0 = e.touches[0].clientX; }, { passive: true });
    stage?.addEventListener('touchend', (e) => {
      if (x0 === null) return;
      const dx = e.changedTouches[0].clientX - x0; x0 = null;
      if (Math.abs(dx) < 40) return;
      const thumbs = $$('[data-thumb]', root);
      const cur = thumbs.findIndex(t => t.getAttribute('aria-current') === 'true');
      const rtl = document.dir === 'rtl';
      const next = thumbs[(cur + ((dx < 0) !== rtl ? 1 : -1) + thumbs.length) % thumbs.length];
      next && selectMedia(next.dataset.thumb);
    });

    // sticky add-to-cart (phones)
    const buy = $('[data-buy]', root);
    if (sticky && buy && 'IntersectionObserver' in window) {
      new IntersectionObserver(([en]) => {
        const show = !en.isIntersecting && en.boundingClientRect.top < 0;
        sticky.classList.toggle('is-on', show);
        sticky.setAttribute('aria-hidden', !show);
        $('button', sticky).tabIndex = show ? 0 : -1;
        document.body.classList.toggle('buy-in-view', en.isIntersecting);   // chat launcher steps aside
      }).observe(buy);
    }

    // share
    root.addEventListener('click', async (e) => {
      const b = e.target.closest('[data-share]');
      if (!b) return;
      const data = { title: b.dataset.title, url: b.dataset.url };
      if (navigator.share) { try { await navigator.share(data); } catch (_) {} }
      else { await navigator.clipboard?.writeText(data.url); window.toast?.('✓'); }
    });
  };
  window.initProduct = initProduct;
  $$('[data-product]').forEach(initProduct);

  /* ================================================================ quick view */
  const qv = document.createElement('dialog');
  qv.className = 'qv';
  qv.setAttribute('aria-label', 'Quick view');
  qv.innerHTML = `<button class="icon-btn icon-btn--ring qv__x" type="button" data-qv-close aria-label="Close"><svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.8" stroke-linecap="round"><path d="M6 6l12 12M18 6 6 18"/></svg></button><div class="qv__body"></div>`;
  document.body.appendChild(qv);
  let qvReturn = null;
  document.addEventListener('click', async (e) => {
    const b = e.target.closest('[data-qv]');
    if (!b || !qv.showModal) return;
    e.preventDefault();
    qvReturn = b;
    const body = $('.qv__body', qv);
    body.innerHTML = '<div class="qv__loading"><span class="spin"></span></div>';
    qv.showModal();
    try {
      const html = await fetch(`${b.dataset.qv}${b.dataset.qv.includes('?') ? '&' : '?'}view=quick`).then(r => r.text());
      const doc = parse(html);
      const sec = $('[data-product]', doc);
      body.innerHTML = '';
      if (!sec) throw new Error();
      body.appendChild(document.importNode(sec, true));
      const root = $('[data-product]', body);
      // quick view closes itself after a successful add
      root.addEventListener('submit', () => setTimeout(() => qv.close(), 400));
      initProduct(root);
      $('.pdp__info h1, .pdp__info h2', body)?.setAttribute('tabindex', '-1');
    } catch (_) {
      location.href = b.dataset.qv;
    }
  });
  qv.addEventListener('click', (e) => { if (e.target === qv || e.target.closest('[data-qv-close]')) qv.close(); });
  qv.addEventListener('close', () => { $('.qv__body', qv).innerHTML = ''; qvReturn?.focus(); });

  /* ================================================================ cart drawer upsell */
  const loadUpsell = async () => {
    const slot = $('#cart-drawer [data-upsell-for]');
    if (!slot || slot.dataset.loaded === slot.dataset.upsellFor) return;
    slot.dataset.loaded = slot.dataset.upsellFor;
    try {
      const html = await fetch(`${S.routes.root}recommendations/products?product_id=${slot.dataset.upsellFor}&limit=6&intent=complementary&section_id=cart-upsell`).then(r => r.text());
      let fresh = $('[data-upsell]', parse(html));
      if (!fresh || !fresh.querySelector('.cd__up')) {
        const rel = await fetch(`${S.routes.root}recommendations/products?product_id=${slot.dataset.upsellFor}&limit=6&section_id=cart-upsell`).then(r => r.text());
        fresh = $('[data-upsell]', parse(rel));
      }
      const inCart = new Set(($('#cart-drawer [data-in-cart]')?.dataset.inCart || '').split(','));
      if (fresh) {
        $$('.cd__up', fresh).forEach(u => { if (inCart.has(u.dataset.productId)) u.remove(); });
        slot.innerHTML = fresh.querySelector('.cd__up') ? fresh.innerHTML : '';
      }
    } catch (_) { /* optional */ }
  };
  new MutationObserver(() => { if ($('#cart-drawer.is-open')) loadUpsell(); })
    .observe($('#cart-drawer') || document.createElement('div'), { attributes: true, attributeFilter: ['class'], childList: true, subtree: true });
  document.addEventListener('click', (e) => {
    const b = e.target.closest('[data-upsell-add]');
    if (!b) return;
    const fd = new FormData(); fd.append('id', b.dataset.upsellAdd); fd.append('quantity', '1');
    addToCart(fd, { button: b }).catch(() => {});
  });

  /* ================================================================ load more (collections) */
  document.addEventListener('click', async (e) => {
    const b = e.target.closest('[data-load-more]');
    if (!b) return;
    e.preventDefault();
    const root = b.closest('[data-filters]');
    const grid = $('.grid', root);
    b.classList.add('is-busy');
    try {
      const url = new URL(b.href, location.origin);
      url.searchParams.set('section_id', root.dataset.section);
      const doc = parse(await fetch(url).then(r => r.text()));
      const cards = $$('.grid > *', doc);
      cards.forEach(c => grid.appendChild(document.importNode(c, true)));
      $$('[data-reveal]', grid).forEach(el => el.classList.add('is-in'));
      const next = $('[data-load-more-wrap]', doc);
      $('[data-load-more-wrap]', root).replaceWith(next ? document.importNode(next, true) : document.createElement('span'));
      cards[0]?.querySelector('a')?.focus({ preventScroll: true });
    } catch (_) { location.href = b.href; }
  });

  /* ================================================================ recently viewed */
  const RV_KEY = 'stacks:recent';
  const rvRead = () => { try { return (JSON.parse(localStorage.getItem(RV_KEY)) || []).filter(h => /^[a-z0-9][a-z0-9-]*$/.test(h) && h !== 'undefined'); } catch (_) { return []; } };
  const current = $('[data-product]')?.dataset.handle;
  if (current) {
    try { localStorage.setItem(RV_KEY, JSON.stringify([current, ...rvRead().filter(h => h !== current)].slice(0, 12))); } catch (_) {}
  }
  $$('[data-recent]').forEach(async (sec) => {
    const handles = rvRead().filter(h => h !== current).slice(0, +sec.dataset.limit || 4);
    if (!handles.length) { sec.hidden = true; return; }
    const cards = await Promise.all(handles.map(h => fetch(`${S.routes.root}products/${h}?view=card`).then(r => r.ok ? r.text() : '').catch(() => '')));
    const grid = $('.grid', sec);
    cards.forEach(html => { const card = $('.card', parse(html)); if (card) grid.appendChild(document.importNode(card, true)); });
    $$('[data-reveal]', grid).forEach(el => el.classList.add('is-in'));
    sec.hidden = !grid.children.length;
  });

  /* ================================================================ testimonials scroller + video embeds + counters */
  document.addEventListener('click', (e) => {
    const b = e.target.closest('[data-scroll]');
    if (b) {
      const sc = $('[data-scroller]', b.closest('section'));
      const dir = document.dir === 'rtl' ? -1 : 1;
      sc?.scrollBy({ left: +b.dataset.scroll * dir * sc.clientWidth * .8, behavior: 'smooth' });
    }
    const v = e.target.closest('[data-video-embed]');
    if (v) {
      const [type, id] = v.dataset.videoEmbed.split(':');
      const src = type === 'youtube' ? `https://www.youtube-nocookie.com/embed/${id}?autoplay=1&rel=0` : `https://player.vimeo.com/video/${id}?autoplay=1`;
      v.outerHTML = `<iframe src="${src}" allow="autoplay; encrypted-media; picture-in-picture" allowfullscreen title="video"></iframe>`;
    }
  });

  /* ================================================================ recommendations */
  $$('[data-recs]').forEach(async (el) => {
    if (el.children.length) return;
    try {
      const html = await fetch(el.dataset.url).then(r => r.text());
      const fresh = $('[data-recs]', parse(html));
      if (fresh && fresh.children.length) {
        el.innerHTML = fresh.innerHTML;
        el.dispatchEvent(new CustomEvent('stacks:content', { bubbles: true }));
      }
      else el.remove();
    } catch (_) { el.remove(); }
  });

  /* ================================================================ predictive search */
  const psInput = $('[data-ps-input]');
  if (psInput && S.routes?.predictiveSearch) {
    const target = $('[data-ps-target]');
    const quick = $('[data-ps-quick]');
    const spin = $('[data-ps-spin]');
    let timer, ctrl, psUnsupported = false;
    const run = async (q) => {
      ctrl?.abort(); ctrl = new AbortController();
      spin.hidden = false;
      try {
        const enc = encodeURIComponent(q);
        let html = '';
        if (!psUnsupported) {
          const r = await fetch(`${S.routes.predictiveSearch}?q=${enc}&section_id=predictive-search&resources[type]=product,collection,page,query&resources[limit]=7&resources[options][unavailable_products]=last`, { signal: ctrl.signal });
          if (r.ok) html = await r.text(); else psUnsupported = true;   // e.g. 417 for Arabic storefronts
        }
        if (psUnsupported) {
          html = await fetch(`${S.routes.search || (S.routes.root || '/') + 'search'}?q=${enc}&type=product&options[prefix]=last&section_id=search-suggest`, { signal: ctrl.signal }).then(r => r.text());
        }
        const res = $('[data-ps-results]', parse(html));
        target.innerHTML = res ? res.outerHTML : '';
        quick.hidden = !!res;
        psInput.setAttribute('aria-expanded', !!res);
      } catch (_) { /* aborted */ }
      finally { spin.hidden = true; }
    };
    psInput.addEventListener('input', () => {
      clearTimeout(timer);
      const q = psInput.value.trim();
      if (q.length < 2) { target.innerHTML = ''; quick.hidden = false; psInput.setAttribute('aria-expanded', false); return; }
      timer = setTimeout(() => run(q), 220);
    });
    psInput.addEventListener('keydown', (e) => {
      if (e.key !== 'ArrowDown') return;
      const first = $('a', target);
      if (first) { e.preventDefault(); first.focus(); }
    });
  }

  /* ================================================================ collection / search filters */
  const initFilters = (filterRoot) => {
  if (!filterRoot || filterRoot.dataset.ready) return;
  filterRoot.dataset.ready='1';
  {
    const sectionId = filterRoot.dataset.section;
    let t, controller, generation=0;
    const load = async (url, push = true) => {
      controller?.abort(); controller=new AbortController(); const id=++generation;
      filterRoot.classList.add('is-busy');
      const u=new URL(url,location.origin); u.searchParams.set('section_id',sectionId);
      try {
        const res=await fetch(u,{signal:controller.signal});
        if (!res.ok) throw new Error(S.t.error);
        const html=await res.text(); if (id!==generation) return;
        const fresh=$('[data-filters]',parse(html));
        if (!fresh) throw new Error(S.t.error);
        const wasOpen=!!$('[data-filters-panel].is-open',filterRoot);
        document.body.classList.remove('filters-open');
        filterRoot.innerHTML=fresh.innerHTML;
        if (wasOpen) $('[data-filters-open]',filterRoot)?.focus();
        u.searchParams.delete('section_id');
        if (push) history.pushState({filters:true},'',u);
        const top=filterRoot.getBoundingClientRect().top;
        if (top<0) scrollTo({top:scrollY+top-90,behavior:'smooth'});
      } catch(e) {if (e.name!=='AbortError' && id===generation) window.toast?.(e.message);}
      finally {if (id===generation) filterRoot.classList.remove('is-busy');}
    };
    const fromForm = (form) => {
      const params = new URLSearchParams(new FormData(form));
      [...params.keys()].forEach(k => { if (params.get(k) === '') params.delete(k); });
      return `${location.pathname}?${params}`;
    };
    filterRoot.addEventListener('change', (e) => {
      const form = e.target.closest('form[data-filter-form]');
      if (!form) return;
      clearTimeout(t);
      t = setTimeout(() => load(fromForm(form)), e.target.type === 'number' ? 600 : 50);
    });
    filterRoot.addEventListener('submit', (e) => {
      const form = e.target.closest('form[data-filter-form]');
      if (!form) return;
      e.preventDefault(); load(fromForm(form));
    });
    filterRoot.addEventListener('click', (e) => {
      const a = e.target.closest('a[data-filter-link]');
      if (a) { e.preventDefault(); load(a.href); }
      const panel = $('[data-filters-panel]', filterRoot);
      const open = e.target.closest('[data-filters-open]');
      if (open) { panel?.classList.add('is-open'); document.body.classList.add('filters-open'); }
      const close = e.target.closest('[data-filters-close]');
      /* the scrim is the panel's own ::before, so a click landing on the panel
         itself (never on its children) means "outside" */
      if (close || (panel?.classList.contains('is-open') && e.target === panel)) {
        panel?.classList.remove('is-open');
        document.body.classList.remove('filters-open');
        $('[data-filters-open]',filterRoot)?.focus();
      }
    });
    const pop=()=>load(location.href,false);
    addEventListener('popstate',pop);
    filterRoot._dispose=()=>{clearTimeout(t);controller?.abort();generation++;removeEventListener('popstate',pop);document.body.classList.remove('filters-open');};
  }
  };
  initFilters($('[data-filters]'));
  document.addEventListener('shopify:section:load',e=>{
    $$('[data-product]',e.target).forEach(initProduct);
    initFilters($('[data-filters]',e.target));
  });
  document.addEventListener('shopify:section:unload',e=> $('[data-filters]',e.target)?._dispose?.());
})();
